import { useMemo, useState } from 'react';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

export type PaymentOutcome = 'succeeded' | 'processing';

interface Props {
  clientSecret: string;
  publishableKey: string;
  stripeAccountId: string;
  onComplete: (outcome: PaymentOutcome) => void;
}

// Cache one Stripe.js instance per (publishableKey, connected account) pair —
// loadStripe is expensive and must not run on every render.
const stripeCache = new Map<string, Promise<Stripe | null>>();
function getStripe(publishableKey: string, stripeAccount: string): Promise<Stripe | null> {
  const key = `${publishableKey}:${stripeAccount}`;
  let cached = stripeCache.get(key);
  if (!cached) {
    cached = loadStripe(publishableKey, { stripeAccount });
    stripeCache.set(key, cached);
  }
  return cached;
}

function PaymentForm({ onComplete }: { onComplete: (o: PaymentOutcome) => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePay = async () => {
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);

    // redirect: 'if_required' keeps the donor on-page for cards (and only
    // redirects for payment methods that mandate it), so we can show our own
    // confirmation screen from the synchronous result.
    const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
    });

    if (confirmError) {
      setError(confirmError.message ?? 'Your payment could not be processed. Please try again.');
      setSubmitting(false);
      return;
    }

    if (paymentIntent?.status === 'succeeded') {
      onComplete('succeeded');
    } else if (paymentIntent?.status === 'processing') {
      onComplete('processing');
    } else {
      setError('Your payment could not be completed. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="payment-section">
      <PaymentElement />
      {error && <p className="field-error" role="alert">{error}</p>}
      <button type="button" className="submit-btn" disabled={!stripe || submitting} onClick={handlePay}>
        {submitting ? 'Processing…' : 'Pay now'}
      </button>
    </div>
  );
}

export function PaymentSection({ clientSecret, publishableKey, stripeAccountId, onComplete }: Props) {
  const stripePromise = useMemo(
    () => getStripe(publishableKey, stripeAccountId),
    [publishableKey, stripeAccountId],
  );

  return (
    <Elements stripe={stripePromise} options={{ clientSecret }}>
      <PaymentForm onComplete={onComplete} />
    </Elements>
  );
}
